if (!customElements.get('free-shipping-upsell')) {

class FreeShippingUpsell extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.unsubscribe = null;
  }

  connectedCallback() {
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || !event.cartData) return;
      this.handleCartUpdate(event.cartData);
    });

    // Wire up add buttons for the server-rendered initial tiles
    this.bindButtons();
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
  }

  /* -------------------------------------------------------
     Handle a cart update: show/hide the block and update tiles
  ------------------------------------------------------- */
  handleCartUpdate(cart) {
    const subtotal = cart.items_subtotal_price || 0;
    const itemCount = cart.item_count || 0;
    const gap = Math.max(0, this.threshold - subtotal);

    if (itemCount === 0 || gap === 0) {
      this.classList.add('is-hidden');
      return;
    }

    this.classList.remove('is-hidden');
    this.refreshTiles(cart, gap);
  }

  /* -------------------------------------------------------
     Re-render the tile grid client-side after an ATC action.
     We remove tiles whose product is now in the cart, and
     tiles whose price would be too high for the new gap.
  ------------------------------------------------------- */
  refreshTiles(cart, gap) {
    const cartProductIds = new Set((cart.items || []).map((i) => i.product_id));
    const maxPrice = gap * 1.5; // same logic as server-side filter

    const grid = this.querySelector('.free-shipping-upsell__grid');
    if (!grid) return;

    const tiles = Array.from(grid.querySelectorAll('.free-shipping-upsell__tile'));
    let visibleCount = 0;

    tiles.forEach((tile) => {
      const productId = parseInt(tile.dataset.productId, 10);
      const variantPrice = parseInt(tile.dataset.variantPrice, 10);

      const shouldHide = cartProductIds.has(productId) || variantPrice > maxPrice;
      tile.hidden = shouldHide;
      if (!shouldHide) visibleCount++;
    });

    // If all tiles are gone, hide the whole block
    if (visibleCount === 0) {
      this.classList.add('is-hidden');
    }
  }

  /* -------------------------------------------------------
     Bind ATC click handlers to all Add buttons
  ------------------------------------------------------- */
  bindButtons() {
    this.querySelectorAll('.free-shipping-upsell__btn').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleAdd(e, btn));
    });
  }

  /* -------------------------------------------------------
     POST /cart/add.js for a single variant, then publish
     cartUpdate so the progress bar and upsell both refresh.
  ------------------------------------------------------- */
  async handleAdd(e, btn) {
    e.preventDefault();

    if (btn.classList.contains('is-loading')) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');

    try {
      // Add to cart
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!addRes.ok) throw new Error(`ATC failed: ${addRes.status}`);

      // Fetch fresh cart state
      const cartRes = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cartData = await cartRes.json();

      // Let the progress bar (and this element) react
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'free-shipping-upsell', cartData });

    } catch (err) {
      console.error('[FreeShippingUpsell] ATC error:', err);
    } finally {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }
}

customElements.define('free-shipping-upsell', FreeShippingUpsell);
}
